/**
 * Module for communicating with Jabra headsets
 * - will use Jabra browser plugin if available and not embedded
 * - will use localhost:61337 api if embedded
 */

import { useSettingsStore } from '@/stores/Settings'
import globalEvents from '@/utilities/GlobalEvents'
import { useEmployeesStore } from '@/stores/Employees'
import logger from '@/utilities/Logger'
import { UAParser } from 'ua-parser-js'
import { EmployeePresenceState } from '@/TcxApi/Employee'
import KuandoWebHID, { BusylightSoundclips, BusylightVolume } from '@/utilities/headsets/KuandoWebHID'
import _i18n from '@/utilities/I18n'
import { sleep } from '../Tools'
import { PbxAppSettingsDesktopResponseKuandoModes, type PbxAppSettingsDesktopResponseKuandoColor } from '@/TcxApi/PbxApp'

const i18n = _i18n.global
const browserDetect = UAParser()
const isSafari = browserDetect.browser.is('Safari')
const isFirefox = browserDetect.browser.is('Firefox')
let webHID: KuandoWebHID | undefined = undefined
let connected = false

export default class Kuando {
	static #instance: Kuando // NOSONAR

	/**
	 * Initialize Kuando
	 */
	private constructor() {
		logger.debug('Kuando: Constructor')
		globalEvents.on('StatusRequest', () => {
			const ss = useSettingsStore()

			globalEvents.emit('StatusReport', { key: 'kuando', value: ss.settings.media.kuando.use })
		})
		globalEvents.on('KuandoPairingRequired', this.performPairing.bind(this))

		this.init()
	}

	/** Get the Kuando instance */
	static get instance() {
		if (!Kuando.#instance) {
			Kuando.#instance = new Kuando()
		}
		return Kuando.#instance
	}

	/** Initialiser */
	private async init() {
		logger.info('Kuando: Initialzing')
		if (isSafari || isFirefox) {
			logger.info('Kuando: Safari or Firefox detected - not supported - init cancelled')
			return
		}

		const ss = useSettingsStore()
		const employees = useEmployeesStore()

		await ss.ready()
		await employees.ready()

		window.addEventListener('beforeunload', () => { this.off() })

		if (employees.me?.currentRingingCall) {
			this.ringing()
		} else if (employees.me?.currentCall) {
			this.busy()
		} else {
			this.idle()
		}
	}

	async startup() {
		const ss = useSettingsStore()
		if (ss.settings.media.kuando.use && !webHID) {
			webHID = new KuandoWebHID()
			const startRes = await webHID.start(false)
			if (!startRes) {
				connected = false
				if (!ss.runtime.isEmbedded) {
					logger.info('Kuando: Need to pair device')
					globalEvents.emit('CommonQuestionModal', {
						header: i18n.t('kuando.pairingHeader'),
						message: i18n.t('kuando.pairingMessage').replace('%APPNAME%', ss.runtime.appName),
						ok: i18n.t('common.ok'),
						emit: 'KuandoPairingRequired'
					})
				} else {
					// Dont need to show paring in embedded mode - this just means that there is no busylight device connected
				}
			} else {
				connected = true
			}
		}
	}

	private async performPairing() {
		if (!webHID) {
			webHID = new KuandoWebHID()
		}

		const startRes2 = await webHID.start(true)

		if (startRes2) {
			logger.info('Kuando: Device paired')
			connected = true
		} else {
			logger.info('Kuando: Device not paired')
			connected = false
		}
	}

	/** Called after settings has been changed to set the current state */
	save() {
		const ss = useSettingsStore()
		const employees = useEmployeesStore()

		if (!ss.settings.media.kuando.use) {
			this.off()
		} else if (employees.me?.currentRingingCall) {
			this.ringing()
		} else if (employees.me?.currentCall) {
			this.busy()
		} else {
			this.idle()
		}
	}

	/**
	 * Set the state of the Kuando device
	 * @param state New state to set, including mode and color(s)
	 * @param soundclip Optional sound to play for ringing
	 * @param volume Optional volume for ringing
	 */
	setState(state: PbxAppSettingsDesktopResponseKuandoColor, soundclip?: BusylightSoundclips, volume?: BusylightVolume) {
		switch (state.mode) {
			case PbxAppSettingsDesktopResponseKuandoModes.COLOR:
				if (state.color) {
					webHID?.ColorRGB(Math.floor(state.color.r / 2.55), Math.floor(state.color.g / 2.55), Math.floor(state.color.b / 2.55), soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.BLINK_EMERGENCY:
				if (state.color) {
					webHID?.Blink({ rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, 1, 1, soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.BLINK_FAST:
				if (state.color) {
					webHID?.Blink({ rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, 5, 5, soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.BLINK_NORMAL:
				if (state.color) {
					webHID?.Blink({ rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, 10, 10, soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.BLINK_SLOW:
				if (state.color) {
					webHID?.Blink({ rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, 20, 20, soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.DISCO:
				webHID?.Sequence([
					{ color: { rgbred: 0, rgbgreen: 0, rgbblue: 100 }, duration: 1 },
					{ color: { rgbred: 0, rgbgreen: 100, rgbblue: 90 }, duration: 1 },
					{ color: { rgbred: 100, rgbgreen: 0, rgbblue: 0 }, duration: 1 },
					{ color: { rgbred: 100, rgbgreen: 100, rgbblue: 0 }, duration: 1 },
					{ color: { rgbred: 0, rgbgreen: 100, rgbblue: 100 }, duration: 1 },
					{ color: { rgbred: 100, rgbgreen: 0, rgbblue: 1000 }, duration: 1 },
					{ color: { rgbred: 100, rgbgreen: 100, rgbblue: 100 }, duration: 1 }
				], soundclip, volume)
				break
			case PbxAppSettingsDesktopResponseKuandoModes.FLASH_FAST:
				if (state.color && state.altColor) {
					webHID?.Sequence([
						{ color: { rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, duration: 5 },
						{ color: { rgbred: Math.floor(state.altColor.r / 2.55), rgbgreen: Math.floor(state.altColor.g / 2.55), rgbblue: Math.floor(state.altColor.b / 2.55) }, duration: 1 }
					], soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.FLASH_SLOW:
				if (state.color && state.altColor) {
					webHID?.Sequence([
						{ color: { rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, duration: 20 },
						{ color: { rgbred: Math.floor(state.altColor.r / 2.55), rgbgreen: Math.floor(state.altColor.g / 2.55), rgbblue: Math.floor(state.altColor.b / 2.55) }, duration: 1 }
					], soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.PULSE_INTENSE:
				if (state.color) {
					webHID?.Pulse({ rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, 33, 1, soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.PULSE_NORMAL:
				if (state.color) {
					webHID?.Pulse({ rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, 20, 1, soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.PULSE_SOFT:
				if (state.color) {
					webHID?.Pulse({ rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, 10, 1, soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.TWO_COLOR_FAST:
				if (state.color && state.altColor) {
					webHID?.Sequence([
						{ color: { rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, duration: 5 },
						{ color: { rgbred: Math.floor(state.altColor.r / 2.55), rgbgreen: Math.floor(state.altColor.g / 2.55), rgbblue: Math.floor(state.altColor.b / 2.55) }, duration: 5 }
					], soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.TWO_COLOR_NORMAL:
				if (state.color && state.altColor) {
					webHID?.Sequence([
						{ color: { rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, duration: 10 },
						{ color: { rgbred: Math.floor(state.altColor.r / 2.55), rgbgreen: Math.floor(state.altColor.g / 2.55), rgbblue: Math.floor(state.altColor.b / 2.55) }, duration: 10 }
					], soundclip, volume)
				}
				break
			case PbxAppSettingsDesktopResponseKuandoModes.TWO_COLOR_SLOW:
				if (state.color && state.altColor) {
					webHID?.Sequence([
						{ color: { rgbred: Math.floor(state.color.r / 2.55), rgbgreen: Math.floor(state.color.g / 2.55), rgbblue: Math.floor(state.color.b / 2.55) }, duration: 20 },
						{ color: { rgbred: Math.floor(state.altColor.r / 2.55), rgbgreen: Math.floor(state.altColor.g / 2.55), rgbblue: Math.floor(state.altColor.b / 2.55) }, duration: 20 }
					], soundclip, volume)
				}
				break
		}
	}

	/**
	 * Called before performing an action
	 * @returns User settings store if Kuando should be used, null if not
	 */
	async shouldIDoIt() {
		const ss = useSettingsStore()

		if (!ss.settings.media.kuando.use) { return null }

		const start = new Date().getTime()

		while (!connected && new Date().getTime() - start < 10000) {
			await sleep(250)
		}

		if (new Date().getTime() - start < 10000) {
			return ss
		} else {
			return null
		}
	}

	/**
	 * Set ringing state
	 */
	async ringing() {
		const ss = await this.shouldIDoIt()

		if (!ss) { return }

		logger.info('Kuando: Setting ringing state')
		if (ss.settings.media.kuando.ringTone > 0) {
			let volume: BusylightVolume = BusylightVolume.Max // max volume - 2=low, 3=middle, 5=high, 6=max

			switch (ss.settings.media.kuando.ringVolume) {
				case 0: volume = BusylightVolume.Mute; break
				case 25: volume = BusylightVolume.Low; break
				case 50: volume = BusylightVolume.Middle; break
				case 75: volume = BusylightVolume.High; break
				case 100: volume = BusylightVolume.Max; break
			}

			logger.info('Kuando: Setting ringing state with volume ' + volume)
			const settings = ss.settings.media.kuando.colorSettings?.ringing || { mode: PbxAppSettingsDesktopResponseKuandoModes.BLINK_FAST, color: { r: 255, g: 255, b: 0 } }

			this.setState(settings, ss.settings.media.kuando.ringTone, volume)
		} else {
			logger.info('Kuando: Setting ringing state')
			const settings = ss.settings.media.kuando.colorSettings?.ringing || { mode: PbxAppSettingsDesktopResponseKuandoModes.BLINK_FAST, color: { r: 255, g: 255, b: 0 } }

			this.setState(settings)
		}
	}

	/**
	 * Set calling out state
	 */
	async calling() {
		const ss = await this.shouldIDoIt()

		if (!ss) { return }

		logger.info('Kuando: Setting calling state')
		const settings = ss.settings.media.kuando.colorSettings?.calling || { mode: PbxAppSettingsDesktopResponseKuandoModes.COLOR, color: { r: 255, g: 255, b: 0 } }

		this.setState(settings)
	}

	/**
	 * Set busy state
	 */
	async busy() {
		const ss = await this.shouldIDoIt()

		if (!ss) { return }

		logger.info('Kuando: Setting busy state')
		const settings = ss.settings.media.kuando.colorSettings?.busy || { mode: PbxAppSettingsDesktopResponseKuandoModes.COLOR, color: { r: 255, g: 0, b: 0 } }

		this.setState(settings)
	}

	/**
	 * Set idle state
	 */
	async idle() {
		const ss = await this.shouldIDoIt()

		if (!ss) { return }

		const employees = useEmployeesStore()

		await ss.ready()
		await employees.ready()

		if (ss.runtime.desktopLocked) {
			logger.info('Kuando: Setting desktop locked state')
			const settings = ss.settings.media.kuando.colorSettings?.desktopLocked || { mode: PbxAppSettingsDesktopResponseKuandoModes.COLOR, color: { r: 0, g: 128, b: 255 } }

			this.setState(settings)
			return
		}

		switch (employees.me?.presenceState) {
			case EmployeePresenceState.AVAILABLE: {
				logger.info('Kuando: Setting available/idle state')
				const settings = ss.settings.media.kuando.colorSettings?.idle || { mode: PbxAppSettingsDesktopResponseKuandoModes.COLOR, color: { r: 0, g: 255, b: 0 } }

				this.setState(settings)
				break
			}
			case EmployeePresenceState.BUSY: {
				logger.info('Kuando: Setting busy state')
				const settings = ss.settings.media.kuando.colorSettings?.busy || { mode: PbxAppSettingsDesktopResponseKuandoModes.COLOR, color: { r: 255, g: 0, b: 0 } }

				this.setState(settings)
				break
			}
			case EmployeePresenceState.OUT_OF_OFFICE: {
				logger.info('Kuando: Setting out of office state')
				const settings = ss.settings.media.kuando.colorSettings?.presenceOutOfOffice || { mode: PbxAppSettingsDesktopResponseKuandoModes.COLOR, color: { r: 255, g: 128, b: 0 } }

				this.setState(settings)
				break
			}
			case EmployeePresenceState.OFF_WORK: {
				logger.info('Kuando: Setting off work state')
				const settings = ss.settings.media.kuando.colorSettings?.presenceOffWork || { mode: PbxAppSettingsDesktopResponseKuandoModes.COLOR, color: { r: 0, g: 0, b: 255 } }

				this.setState(settings)
				break
			}
			case EmployeePresenceState.HOLIDAY: {
				logger.info('Kuando: Setting holiday state')
				const settings = ss.settings.media.kuando.colorSettings?.presenceHoliday || { mode: PbxAppSettingsDesktopResponseKuandoModes.COLOR, color: { r: 0, g: 0, b: 255 } }

				this.setState(settings)
				break
			}
		}
	}

	/**
	 * Turn off
	 */
	async off() {
		const ss = await this.shouldIDoIt()

		if (!ss) { return }

		logger.info('Kuando: Turning off')
		webHID?.ColorRGB(0, 0, 0)
	}
}
